import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import useScribeService from '../../../hooks/useScribeService';
import logger from '../../../utils/logger';

const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const AUTO_SAVE_INTERVAL_MS = 30 * 1000; // 30 seconds

const useScribeSession = (siteId, mkey, intakeReport, verbosity, noteTemplate) => {
  const [isRecording, setIsRecording] = useState(false);
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState(null);
  const [isGeneratingNote, setIsGeneratingNote] = useState(false);
  const [showInactivityDialog, setShowInactivityDialog] = useState(false);
  const [analyzerNode, setAnalyzerNode] = useState(null);

  const transcriber = useRef(null);
  const service = useScribeService();
  const inactivityTimeout = useRef(null);
  const autoSaveInterval = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const lastSavedTranscript = useRef('');
  const recordingRef = useRef(false);
  const linesRef = useRef([]);

  useEffect(() => {
    recordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  const saveTranscript = useCallback(async (linesToSave) => {
    if (!service || linesToSave.length === 0) return;
    const transcript = linesToSave.map((l) => l.text).join('\n');
    if (transcript === lastSavedTranscript.current) return;
    await service.saveTranscript(siteId, mkey, transcript);
    lastSavedTranscript.current = transcript;
  }, [siteId, mkey, service]);

  const cleanupAudioResources = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setAnalyzerNode(null);
  }, []);

  const stopRecording = useCallback((dueToInactivity = false) => {
    saveTranscript(linesRef.current).catch((err) => {
      logger.error('Failed to save transcript on stop:', err);
    });

    transcriber.current?.stopTranscribingAsync(() => {
      transcriber.current?.close();
      transcriber.current = null;
      setIsRecording(false);

      cleanupAudioResources();

      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
        inactivityTimeout.current = null;
      }
      if (autoSaveInterval.current) {
        clearInterval(autoSaveInterval.current);
        autoSaveInterval.current = null;
      }

      if (dueToInactivity) {
        setShowInactivityDialog(true);
      }
    });
  }, [saveTranscript, cleanupAudioResources]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
    }
    inactivityTimeout.current = setTimeout(() => {
      if (recordingRef.current) {
        stopRecording(true);
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [stopRecording]);

  useEffect(() => {
    service.getOrCreateSession(siteId, mkey).then((session) => {
      if (session.transcript) {
        const parsedLines = session.transcript.split('\n').filter(Boolean).map((l, i) => ({ id: i, text: l }));
        setLines(parsedLines);
        lastSavedTranscript.current = session.transcript;
      }
      if (session.note) {
        setNote(session.note);
      }
    });
    return () => {
      transcriber.current?.close();
      cleanupAudioResources();
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
      }
      if (autoSaveInterval.current) {
        clearInterval(autoSaveInterval.current);
      }
    };
  }, [siteId, mkey, service, cleanupAudioResources]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 256;
      analyzer.smoothingTimeConstant = 0.8;
      source.connect(analyzer);
      setAnalyzerNode(analyzer);
    } catch (err) {
      logger.error('Failed to setup audio visualization:', err);
      cleanupAudioResources();
      return;
    }

    try {
      const t = await service.createTranscriber();
      transcriber.current = t;

      t.transcribing = (_, e) => {
        if (e.result.text?.trim()) {
          resetInactivityTimer();
        }
      };

      t.transcribed = (_, e) => {
        if (e.result.text?.trim()) {
          const speaker = e.result.speakerId || 'Guest';
          setLines((prev) => [...prev, { id: Date.now(), text: `[${speaker}]: ${e.result.text}` }]);
          resetInactivityTimer();
        }
      };

      t.startTranscribingAsync(() => {
        setIsRecording(true);
        resetInactivityTimer();

        autoSaveInterval.current = setInterval(() => {
          saveTranscript(linesRef.current);
        }, AUTO_SAVE_INTERVAL_MS);
      });
    } catch (err) {
      logger.error('Failed to start transcription:', err);
      cleanupAudioResources();
    }
  }, [service, cleanupAudioResources, resetInactivityTimer, saveTranscript]);

  const stop = useCallback(() => {
    stopRecording(false);
  }, [stopRecording]);

  const generateNote = useCallback(async () => {
    if (isRecording && transcriber.current) {
      stopRecording(false);
    }

    setIsGeneratingNote(true);
    try {
      await saveTranscript(lines);
      const result = await service
        .generateNote(siteId, mkey, intakeReport, verbosity, noteTemplate);
      setNote(result.note);
    } finally {
      setIsGeneratingNote(false);
    }
  }, [isRecording, stopRecording, saveTranscript, lines, service,
    siteId, mkey, intakeReport, verbosity, noteTemplate]);

  const dismissDialog = useCallback(() => {
    setShowInactivityDialog(false);
  }, []);

  const hasExistingTranscript = lines.length > 0;

  return {
    isRecording,
    note,
    isGeneratingNote,
    hasExistingTranscript,
    showInactivityDialog,
    analyzerNode,
    start,
    stop,
    generateNote,
    dismissDialog,
  };
};

export default useScribeSession;
